import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { EstimationSchemesApi } from '@/lib/api/common/estimationScheme';
import type { EstimationScheme } from '@/lib/api/types/estimationScheme';
import { useFlag } from '@/lib/hooks/feature-flags/useFlag';
import { commonQueryKeys } from '@/lib/query-keys/common';

const getDefaultScheme = (schemes: EstimationScheme[]): EstimationScheme | undefined =>
  schemes.find((s) => s.isOrganizationDefault) ?? schemes[0];

export const getEstimationSchemesQueryOptions = () => {
  return {
    queryKey: commonQueryKeys.estimationSchemes.all(),
    queryFn: () => EstimationSchemesApi.getEstimationSchemes(),
  };
};

export const useGetEstimationSchemesQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<EstimationScheme[]>({
    ...getEstimationSchemesQueryOptions(),
    enabled,
  });
};

export const useSuspenseGetEstimationSchemesQuery = () => {
  return useSuspenseQuery<EstimationScheme[]>({
    ...getEstimationSchemesQueryOptions(),
  });
};

export const useGetEstimationSchemeQuery = ({
  schemeId,
  staleTime,
}: {
  schemeId: number;
  staleTime?: number;
}) => {
  return useQuery<EstimationScheme>({
    queryKey: commonQueryKeys.estimationSchemes.details(schemeId),
    queryFn: () => EstimationSchemesApi.getSchemeDetails({ schemeId }),
    enabled: schemeId != null && schemeId !== 0,
    staleTime,
  });
};

export const useEstimationsQuery = ({
  enabled = true,
  staleTime,
}: {
  enabled?: boolean;
  staleTime?: number;
} = {}) => {
  const isMultiSchemeEnabled = useFlag('itemStoreEstimations');

  return useQuery({
    queryKey: commonQueryKeys.estimationSchemes.estimations,
    queryFn: () => EstimationSchemesApi.getEstimationSchemes(),
    select: (data) =>
      isMultiSchemeEnabled
        ? data.flatMap((d) => d.estimations)
        : (getDefaultScheme(data)?.estimations ?? []),
    enabled,
    staleTime,
  });
};
